import React, { Fragment, useState, useEffect } from 'react'
import useApi from 'hooks/useApi'
import messages from './messages'
import { FormattedMessage, useIntl } from 'react-intl'
import { useKeyboardShortcut } from '@arnsbogroup/react-voicearchive/lib/hooks'
import useCurrentUser from 'hooks/useCurrentUser'
import Tabs from 'components/TabsRoutes'
import {
  Card as InitialCard,
  Icon,
  InputInline,
  SelectAsyncInline,
  SelectInline,
  TextArea,
  Button,
  MenuButton,
  Chip,
  Avatar,
} from '@arnsbogroup/react-voicearchive'
import { Link } from 'react-router-dom'
import DateTime from 'react-datetime'
import debounce from 'debounce-promise'
import classNames from 'classnames'
import OrderDetailsPane from 'containers/CustomerOrdersPage/components/OrderDetailsPane'
import Files from 'components/Files'
import OrderFlagFragments from 'components/OrderFlagFragments'
import LogPane from '../LogPane'
import IncidentsPane from '../IncidentsPane'
import LandingPagePane from '../LandingPagePane'
import CommunicationPane from '../CommunicationPane'
import CustomerPage from 'containers/CustomerPage'
import AuditionScopePage from 'containers/AuditionScopePage'
import Stats from '../Stats'

import DraftOrderButton from './components/DraftOrderButton'
import InvoiceOrderButton from './components/InvoiceOrderButton'
import CloseOrderButton from './components/CloseOrderButton'

import CopyLinkMenuItem from './../CopyLinkMenuItem'
import DuplicateOrder from './components/DuplicateOrder'
import MoveOrder from './components/MoveOrder'
import PurgeOrder from './components/PurgeOrder'
import CreateDeal from './components/CreateDeal'
import RestoreArchivedFiles from './components/RestoreArchivedFiles'
import AutoDeliver from './components/AutoDeliver'
import DeliverAllTasks from './components/DeliverAllTasks'
import ExcludeFromHeyLoyalty from './components/ExcludeFromHeyLoyalty'
import HoldOrder from './components/HoldOrder'
import ForceTbiAndInvoice from './components/ForceTbiAndInvoice'
import HandoverWidget from './components/HandoverWidget'
import ProjectWidget from './components/ProjectWidget'

const Card = ({ children }) => (
  <InitialCard className="p-4 bg-white space-y-2">
    { children }
  </InitialCard>
)

import { Menu, Transition } from '@headlessui/react'

const OverviewPage = ({ onChange, order, loading, readOnly, match, location, refetch }) => {
  const { roles = [], features = [] } = useCurrentUser()
  const { formatMessage } = useIntl()
  const [partners, setPartners] = useState([])
  const { cost, price, priceNet, grossProfit, grossProfitPct, discount, discountPct, currency } = order.stats

  const stats = [
    { title: 'Net Price', primary: `${priceNet} ${currency}`, secondary: `${price} ${currency}`, stat: null, changeType: '+' },
    { title: 'Gross Profit', primary: `${grossProfit} ${currency}`, secondary: null, stat: `${grossProfitPct}%`, changeType: grossProfitPct > 40 ? '+' : '-' },
    { title: 'Cost', primary: `${cost} ${currency}`, secondary: order.cost_forecast, stat: null, changeType: '+' },
    { title: 'Discount', primary: `${discount} ${currency}`, secondary: null, stat: `${discountPct}%`, changeType: discountPct > 25 ? '-' : '+'  },
  ]

  useEffect(() => {
    fetch(`/rest/v1/partner`)
      .then((response) => response.json())
      .then((items) => items.map(({ uid: value, name: label }) => ({ value, label })))
      .then((items) => setPartners(items))
  }, [])

  return (
    <div className="bg-gray-50 w-full h-full space-y-4 flex flex-col overflow-y-auto">
      <div className="w-full p-4 lg:flex lg:items-center lg:justify-between shrink-0">
        <div className="min-w-0 flex-1">
          <h2 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            { loading ? (
              <span>Loading..</span>
            ) : (
              <InputInline 
                disabled={readOnly}
                className="w-full"
                placeholder={formatMessage(messages.emptyOrderName)}
                value={order.name}
                onChange={({ target: { value } }) => {
                  onChange({ ...order, name: value })
                }}
              />
            ) }
          </h2>
          <div className="mt-1 flex flex-col sm:mt-0 sm:flex-row sm:flex-wrap sm:space-x-6">
            <div className="mt-2 flex items-center text-sm text-gray-500"  title={formatMessage(messages.orderId)}>
              <Icon icon={['fas', 'hashtag']} className="mr-1.5 shrink-0 text-gray-400" aria-hidden="true" />
              { order.uid }
            </div>
            { order?.deal_id?.length &&
              <div className="mt-2 flex items-center text-sm text-gray-500" title={formatMessage(messages.dealId)}>
                <Icon icon={['fas', 'link']} className="mr-1.5 shrink-0 text-gray-400" aria-hidden="true" />
                <a
                  href={`https://my.voicearchive.com/crm/deal/details/${order.deal_id}/`}
                  target="_blank"
                  className="text-primary hover:text-primary-darker"
                >
                  D{ order.deal_id }
                </a>
              </div>
            }

            <Link to={`/customers/${order.customer.uid}`}>
              <div className="mt-2 flex items-center text-sm text-gray-500 hover:text-primary-darker" title={formatMessage(messages.customer)}>
                <Icon icon={['fas', 'building']} className="mr-1.5 shrink-0 text-gray-400" aria-hidden="true" />
                <span>{ order.customer.name }</span>
              </div>
            </Link>

            <div className="mt-2 flex items-center text-sm text-gray-500" title={formatMessage(messages.partner)}>
              <Icon icon={['fas', 'briefcase']} className="mr-1.5 shrink-0 text-gray-400" aria-hidden="true" />
              <SelectInline
                disabled={readOnly}
                value={partners.find((p) => p.value == order.partner)}
                onChange={({ value }) => onChange({ ...order, partner: value })}
                options={partners}
                isClearable
              />
            </div>

            <div className="mt-2 flex items-center text-sm text-gray-500" title={formatMessage(messages.deadline)}>
              <Icon icon={['fas', 'calendar']} className="mr-1.5 shrink-0 text-gray-400" aria-hidden="true" />
              <DateTime
                value={new Date(+order.deadline * 1000)}
                inputProps={{
                  disabled: readOnly && !order?.isDraft,
                  className: classNames('bg-transparent', {
                    'border-b border-dashed border-gray-300 cursor-pointer hover:border-gray-500': !readOnly || order?.isDraft
                  })
                }}
                onChange={(value) => onChange({ ...order, deadline: Math.round(new Date(value).getTime() / 1000) })}
                closeOnTab
              />
            </div>

            <div className="mt-2 flex items-center text-sm text-gray-500" title={formatMessage(messages.po)}>
              <Icon icon={['fas', 'receipt']} className="mr-1.5 shrink-0 text-gray-400" aria-hidden="true" />

              <InputInline
                disabled={readOnly}
                type="text"
                placeholder="P.O."
                value={order.customerReference}
                onChange={({ target: { value } }) => onChange({ ...order, customerReference: value })}
              />
            </div>

            <div className="mt-2 flex space-x-1 items-center text-sm text-gray-500" title={formatMessage(messages.status)}>
              <Icon icon={['fas', 'tags']} className="mr-1.5 shrink-0 text-gray-400" aria-hidden="true" />

              <OrderFlagFragments order={order} />
            </div>
          </div>
        </div>

        <div className="mt-5 flex lg:mt-0 lg:ml-4 shrink-0">
          <span className="sm:ml-3">
            { order.isDraft ? (
              <DraftOrderButton size="sm" order={order} refetch={refetch}></DraftOrderButton>
            ) : !order.tasks?.length ? (
              <Button size="sm" disabled>No actions</Button>
            ) : (!order.invoiced && !order.readyforinvoicing) ? (
              <InvoiceOrderButton order={order} disabled={readOnly} refetch={refetch} />
            ) : (
              <CloseOrderButton order={order} disabled={readOnly} refetch={refetch} />
            ) }
          </span>

          <Menu as="div" className="relative ml-3">
            <Menu.Button>
              <Button variant="outline" size="sm" icon={['fal', 'ellipsis-vertical']} iconPosition="right">
                More
              </Button>
            </Menu.Button>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-200"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-0 z-10 mt-2 -mr-1 w-80 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <CopyLinkMenuItem />
                <DuplicateOrder order={order} />
                <CreateDeal order={order} readOnly={readOnly} />
                <RestoreArchivedFiles order={order} readOnly={readOnly} />
                <AutoDeliver order={order} readOnly={readOnly} />
                <DeliverAllTasks order={order} refetch={refetch} readOnly={readOnly} />
                <ExcludeFromHeyLoyalty order={order} refetch={refetch} readOnly={readOnly} />

                <Menu.Item disabled>
                  {(props) => (
                    <MenuButton {...props} disabled className="cursor-help">
                      Danger zone
                    </MenuButton>
                  )}
                </Menu.Item>
                {!!order.canBeClosed &&
                  <Menu.Item>
                    {(props) => (
                      <CloseOrderButton as={MenuButton} order={order} disabled={readOnly} refetch={refetch} {...props} />
                    )}
                  </Menu.Item>
                }
                <HoldOrder order={order} refetch={refetch} readOnly={readOnly}/>
                { roles.includes('finance') &&
                  <>
                    <ForceTbiAndInvoice order={order} refetch={refetch} />
                    <MoveOrder order={order} refetch={refetch} />
                  </>
                }
                { roles.includes('admin') &&
                  <PurgeOrder order={order} />
                }
              </Menu.Items>
            </Transition>
          </Menu>
        </div>
      </div>

      <Tabs
        className="bg-gray-50 grow h-full overflow-y-auto flex flex-col"
        childrenClassName="bg-white grow h-full overflow-y-auto"
        tabsClassName="px-4 shrink-0"
        tabs={[
          {
            message: 'Overview',
            to: `/orders/${order.uid}/overview`,
            route: {
              path: '/orders/:orderId/overview',
              render: (props) => (
                <div className="grid grid-cols-1 gap-6">
                  <Stats stats={stats} />

                  <div className="grid grid-cols-3 gap-6 p-4">
                    <InitialCard className="p-4 bg-yellow-100 border-yellow-300 text-sm text-gray-700 space-y-4">
                      <div className="flex items-center justify-between text-gray-700">
                        <Icon icon={['fal', 'comment']} className="text-base" />
                        <span className="text-xs font-medium">Order note</span>
                      </div>

                      <TextArea
                        disabled={loading}
                        className="bg-yellow-100 p-0 border-0 text-xs text-gray-700 font-mono"
                        rows={8}
                        defaultValue={order.comments}
                        placeholder="Add order note.."
                        onBlur={({ target: { value } }) => {
                          onChange({ ...order, comments: value })
                        }}
                      />
                    </InitialCard>

                    <div className="p-4 space-y-6">
                      { ['coordinator', 'cccoordinator', 'contact', 'cccontact'].map((key) => (
                        <div className="flex items-center space-x-2">
                          <Avatar src={order.contacts[key]?.image} />

                          <SelectAsyncInline
                            disabled={readOnly}
                            value={!order.contacts[key] ? null : {
                              value: order.contacts[key].id,
                              label: order.contacts[key].name,
                            }}
                            onChange={(value) => onChange({
                              ...order,
                              [key]: !value ? null : value?.data?.uid,
                              contacts: {
                                ...order.contacts,
                                [key]: !value ? null : value?.data,
                              }
                            })}
                            label={key in messages ? formatMessage(messages[key]) : key}
                            placeholder={formatMessage(messages.userPlaceholder)}
                            isClearable={key !== 'coordinator'}
                            cacheOptions
                            defaultOptions
                            loadOptions={
                              debounce((query) => fetch({
                                contact: `/rest/v1/customer/${order.customer.uid}/contacts`,
                                cccontact: `/rest/v1/customer/${order.customer.uid}/contacts`,
                                coordinator: `/rest/v1/coordinator?pageSize=100&search[name]=${query}`,
                                cccoordinator: `/rest/v1/coordinator?pageSize=100&search[name]=${query}`,
                              }[key])
                                .then((response) => response.json())
                                .then((items) => items.map((data) => ({
                                  value: data.uid,
                                  label: data.name,
                                  data,
                                })))
                                , 250)
                            }
                          />
                        </div>
                      )) }
                    </div>

                    <div className="p-4 space-y-6">
                      { [
                        ['mediaTypes', 'ordertargetmedia', ['search[hidden]=0', 'search[deleted]=0']],
                        ['audioFormatTypes', 'audioformat', ['search[hidden]=0', 'search[deleted]=0']],
                        ['services', 'order_services', ['search[hidden]=0']],
                        ['products', 'order_target_products', ['search[hidden]=0']]
                      ].map(([key, resource, params]) => (
                        <SelectAsyncInline
                          disabled={readOnly}
                          value={!order[key] ? null : order[key].map(({ uid, name }) => ({
                            value: uid,
                            label: name,
                          })).filter((item) => item.value)}
                          onChange={(values) => onChange({
                            ...order,
                            [key]: !values ? [] : values.map(({ label, value }) => ({
                              name: label,
                              uid: value,
                            })),
                          })}
                          label={key in messages ? formatMessage(messages[key]) : key}
                          placeholder={formatMessage(messages.relationPlaceholder)}
                          isClearable
                          isMulti
                          cacheOptions
                          defaultOptions
                          loadOptions={
                            debounce((query) => fetch(`/rest/v1/${resource}?pageSize=999&search[name]=${query}&${params.join('&')}`)
                              .then((response) => response.json())
                              .then((items) => items.map((data) => ({
                                value: data.uid,
                                label: data.name,
                                data,
                              })))
                              , 250)
                          }
                        />
                      )) }
                      <SelectAsyncInline
                          disabled={readOnly}
                          value={!order.orderUsageType ? null : {label: order.orderUsageType.name, value: order.orderUsageType.uid}}
                          onChange={(value) => onChange({
                            ...order,
                            orderusage: !value ? null : value.value
                          })}
                          label={formatMessage(messages.orderUsageType)}
                          placeholder={formatMessage(messages.relationPlaceholder)}
                          isClearable
                          cacheOptions
                          defaultOptions
                          loadOptions={
                            debounce((query) => fetch(`/rest/v1/orderusage?pageSize=999&search[deleted]=0&search[name]=${query}`)
                              .then((response) => response.json())
                              .then((items) => items.map((data) => ({
                                value: data.uid,
                                label: data.name
                              })))
                              , 250)
                          }
                        />
                    </div>

                    <InitialCard className="p-4 border-yellow-300 text-sm text-gray-700 space-y-4">
                      <div className="flex items-center justify-between text-gray-700">
                        <Icon icon={['fas', 'file-invoice']} className="text-base" />
                        <span className="text-xs font-medium">Invoice</span>
                      </div>

                      <TextArea
                        disabled={readOnly}
                        className="p-0 border-0 text-xs text-gray-700 font-mono"
                        rows={8}
                        defaultValue={order.invoice_text}
                        placeholder="Any text entered here will appear on the invoice..."
                        onBlur={({ target: { value } }) => {
                          onChange({ ...order, invoice_text: value })
                        }}
                      />
                    </InitialCard>

                    { order.deal_id &&
                      <HandoverWidget order={order} />
                    }

                    <ProjectWidget order={order} />
                  </div>
                </div>
              ),
            }
          },
          features.includes('audition-overview') ?
          {
            message: 'Audition Overview',
            to: `/orders/${order.uid}/audition-scope`,
            route: {
              path: '/orders/:orderId/audition-scope',
              render: () => (
                <AuditionScopePage orderId={order.uid} refetchOrder={refetch}/>
              )
            }
          } : null,
          {
            message: 'Incidents',
            to: `/orders/${order.uid}/incidents`,
            route: {
              path: '/orders/:orderId/incidents',
              component: IncidentsPane,
            }
          },
          {
            message: 'Company',
            to: `/orders/${order.uid}/customer`,
            route: {
              path: '/orders/:orderId/customer',
              render: (props) => (
                <CustomerPage customerId={order.customer.uid}/>
              )
            }
          },
          {
            message: 'Communication',
            to: `/orders/${order.uid}/communication`,
            route: {
              path: '/orders/:orderId/communication',
              component: CommunicationPane,
            }
          },
          {
            message: 'Log',
            to: `/orders/${order.uid}/log`,
            route: {
              path: '/orders/:orderId/log',
              component: LogPane,
            }
          },
          {
            message: 'Landing Page',
            to: `/orders/${order.uid}/landing-page`,
            route: {
              path: '/orders/:orderId/landing-page',
              component: LandingPagePane,
            }
          },
          {
            message: 'Files',
            to: `/orders/${order.uid}/files`,
            route: {
              path: '/orders/:orderId/files',
              render: (props) => (
                <Files orderId={order.uid} />
              ),
            }
          },
          {
            message: 'VoiceFlow Chat',
            to: `/orders/${order.uid}/chat`,
            route: {
              path: '/orders/:orderId/chat',
              render: (props) => (
                <OrderDetailsPane order={order} />
              ),
            }
          },
          {
            message: 'Project',
            to: `/orders/${order.uid}/project`,
            route: {
              path: '/orders/:orderId/project',
              render: (props) => (
                <h1>Integration with Voice Finder 2.0 Project coming soon.</h1>
              ),
            }
          },
        ].filter((tab) => tab)}
      />
    </div>
  )
}

export default OverviewPage
